import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { LoginService } from '@uoa/auth';
import { environment } from '../../../../environments/environment';
import { Prompt } from '../../../domain/interfaces/prompts';
import { of } from 'rxjs';
import { catchError, concatMap, tap } from 'rxjs/operators';
import { ToastController, IonicModule } from '@ionic/angular';
import { ConfirmationDialogService } from './confirmation-dialog/confirmation-dialog.service';
import { FormsModule } from '@angular/forms';
import { AudioPlayerComponent } from '../audio-player/audio-player.component';

export interface TimeKeeper {
  isoTime: string;
  datetime: number | string;
  disabled: boolean;
}

@Component({
  selector: 'app-prompt-edit',
  templateUrl: './prompt-edit.component.html',
  styleUrls: ['./prompt-edit.component.scss'],
  imports: [FormsModule, AudioPlayerComponent, IonicModule],
})
export class PromptEditComponent implements OnInit {
  private httpClient = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private loginService = inject(LoginService);
  private toastController = inject(ToastController);
  private confirmationDialog = inject(ConfirmationDialogService);
  private cdRef = inject(ChangeDetectorRef);

  // dialogRef: DialogRef<ConfirmationDialogComponent>;

  // Prompts
  public prompt: Prompt;
  public promptEdit: Prompt;

  start: TimeKeeper;
  end: TimeKeeper;
  isLoaded = false;

  absoluteMinDate: string;
  absoluteMaxDate: string;
  changeableIsStart = false;

  public api: Data;

  ngOnInit() {
    this.route.data.subscribe((api) => {
      console.log('Got API: ', api);
      this.api = api;

      // Use explicit wide bounds so historical/future dates remain selectable across portals.
      this.absoluteMinDate = this.toLocalIsoString(new Date(1970, 0, 1, 0, 0, 0));
      this.absoluteMaxDate = this.toLocalIsoString(new Date(2099, 11, 31, 23, 59, 59));

      if (environment.wallboards.prompt.useMockData) {
        console.log('mock data');
        console.log(environment.wallboards.prompt.audit.mockData.data.Items);

        // Get prompt from route params
        this.prompt = environment.wallboards.prompt.prompts.mockData.data.Items.find(
          (item: { keyword?: string; flow_name?: string }) =>
            item?.keyword === this.route.snapshot.params.keyword && item?.flow_name === this.route.snapshot.params.flowName,
        ) as unknown as Prompt;
        this.promptEdit = this.prompt;
        console.log(this.prompt);
      } else {
        console.log('real data');

        // Get the API prompt url
        const promptUrl = api.prompts.baseUrl + '/' + this.route.snapshot.params.flowName + '/id/' + this.route.snapshot.params.keyword;

        of(null)
          .pipe(
            // Check if the user is logged in
            tap(async () => {
              if (!(await this.loginService.isAuthenticated())) {
                await this.loginService.doLogin();
              }
            }),
            concatMap(() =>
              this.httpClient.get<Prompt>(promptUrl).pipe(
                tap((prompt) => {
                  console.log('Got prompt', prompt[0]);
                  this.prompt = prompt[0];
                  // Set the new promptEdit
                  this.promptEdit = {
                    ...this.prompt,
                  } as Prompt;

                  this.promptEdit.default_prompt = this.formatPrompt(this.promptEdit.default_prompt);
                  this.promptEdit.custom_prompt = this.formatPrompt(this.promptEdit.custom_prompt);

                  // Set the start and end times
                  this.start = {
                    isoTime:
                      !this.prompt.start_time || this.prompt.start_time === '' || this.prompt.start_time == null
                        ? this.toLocalIsoString(Date.now())
                        : this.convertToDate(this.prompt.start_time),
                    datetime:
                      !this.prompt.start_time || this.prompt.start_time === '' || this.prompt.start_time == null
                        ? this.unixNow()
                        : this.toUnixSeconds(this.prompt.start_time),
                    disabled: true,
                  };

                  console.log('start', this.start.isoTime);

                  this.end = {
                    isoTime:
                      !this.prompt.end_time || this.prompt.end_time === '' || this.prompt.end_time == null
                        ? this.toLocalIsoString(new Date())
                        : this.convertToDate(this.prompt.end_time),
                    datetime:
                      !this.prompt.end_time || this.prompt.end_time === '' || this.prompt.end_time == null
                        ? this.unixNow()
                        : this.toUnixSeconds(this.prompt.end_time),
                    disabled: true,
                  };

                  console.log('end', this.end.isoTime);
                  console.log('start done', this.start.isoTime);
                  console.log('end done', this.end.isoTime);

                  this.start.disabled = !this.prompt.start_time || this.prompt.start_time === '';
                  this.end.disabled = !this.prompt.end_time || this.prompt.end_time === '';

                  console.log(this.end.datetime);
                }),
              ),
            ),

            catchError((err) => {
              console.log('Error getting prompt', err);
              return of(null);
            }),
          )
          .subscribe({
            next: () => {
              console.log('done');
              this.isLoaded = true;
            },
          });
      }
    });
  }

  onDatetimeChange(event: Event, isStart: boolean) {
    event.preventDefault();
    this.changeableIsStart = isStart;
    const value = this.getEventDateTimeValue(event);

    // If the selected value is blank, keep boundaries coherent.
    if (!value) {
      // Check if start time is after end time and change start to day before if so
      if (this.start.datetime > this.end.datetime) {
        this.start.isoTime = this.subtractDaysIso(this.end.isoTime, 1);
        this.start.datetime = this.toUnixSeconds(this.start.isoTime);
        console.log(this.start.isoTime);
      }

      // Check if end time is before start time and change end to day after if so
      if (this.end.datetime < this.start.datetime) {
        this.end.isoTime = this.addDaysIso(this.start.isoTime, 1);
        this.end.datetime = this.toUnixSeconds(this.end.isoTime);
        console.log(this.end.isoTime);
      }
    } else {
      const isotime = this.getRoundTime(value);
      const datetime = this.toUnixSeconds(isotime);

      // Save date to prompt
      if (isStart) {
        this.start.datetime = datetime;
        this.start.isoTime = isotime;
        console.log(this.start.isoTime);
        console.log(this.start.datetime);
      } else {
        this.end.datetime = datetime;
        this.end.isoTime = isotime;
        console.log(this.end.isoTime);
        console.log(this.end.datetime);
      }

      this.ensureDistinctDateTimes(isStart);
    }

    this.cdRef.detectChanges();
  }

  // Convert unix timestamp to date string
  convertToDate(timestamp: number | string): string {
    if (typeof timestamp === 'number' && isNaN(timestamp)) {
      timestamp = Date.now();
    }
    return this.toLocalIsoString(timestamp);
  }

  // Date time set to 0
  onDatetimeClear(event: Event, isStart: boolean) {
    event.preventDefault();

    if (isStart) {
      this.start.disabled = !this.start.disabled;
    } else {
      this.end.disabled = !this.end.disabled;
    }
  }

  submit($event) {
    $event.preventDefault();

    this.confirmationDialog.keyword = this.prompt.keyword;
    this.confirmationDialog.flow = this.prompt.flow_name;

    // Only compare boundaries when both start and end are enabled.
    if (!this.start.disabled && !this.end.disabled) {
      if (this.isAfter(this.start.isoTime, this.end.isoTime)) {
        this.toastController
          .create({
            message: 'Start time cannot be after end time',
            duration: 3000,
            color: 'danger',
          })
          .then(async (toast) => {
            await toast.present();
          });
        return;
      }

      // Check if end time is before start time and throw error if so
      if (this.isBefore(this.end.isoTime, this.start.isoTime)) {
        this.toastController
          .create({
            message: 'End time cannot be before start time',
            duration: 3000,
            color: 'danger',
          })
          .then(async (toast) => {
            await toast.present();
          });
        return;
      }
      if (this.end.isoTime === this.start.isoTime) {
        this.toastController
          .create({
            message: 'End time cannot be the same as start time',
            duration: 3000,
            color: 'danger',
          })
          .then(async (toast) => {
            await toast.present();
          });
        return;
      }
    }

    this.confirmationDialog.open();

    this.confirmationDialog.dialogRef.afterClosed().subscribe((isAccepted) => {
      if (isAccepted) {
        // Get the keyword and flow name from the route params
        const keyword = this.route.snapshot.params.keyword;
        const flowName = this.route.snapshot.params.flowName;

        // Get the URL to update the prompt
        const promptUrl = this.api.prompts.baseUrl + '/' + flowName + '/id/' + keyword;

        let default_prompt = this.promptEdit.default_prompt;

        // Check if this.text starts with and ends with <speak> and </speak> and add them if not
        if (!default_prompt.startsWith('<speak>')) {
          default_prompt = '<speak>' + default_prompt;
        }
        if (!default_prompt.endsWith('</speak>')) {
          default_prompt = default_prompt + '</speak>';
        }

        let custom_prompt = this.promptEdit.custom_prompt;

        // Check if this.text starts with and ends with <speak> and </speak> and add them if not
        if (!custom_prompt.startsWith('<speak>')) {
          custom_prompt = '<speak>' + custom_prompt;
        }
        if (!custom_prompt.endsWith('</speak>')) {
          custom_prompt = custom_prompt + '</speak>';
        }

        // Send to api
        this.httpClient
          .put(promptUrl, {
            default_prompt: default_prompt,
            custom_prompt: custom_prompt,
            // Start and end times as unix timestamp
            start_time: this.start.disabled ? '' : Number(this.start.datetime),
            end_time: this.end.disabled ? '' : Number(this.end.datetime),
          })
          .subscribe({
            next: (data: Prompt) => {
              console.log('after put', data);
              this.prompt = data;

              this.toastController
                .create({
                  message: 'Successfully updated prompt',
                  position: 'top',
                  duration: 2000,
                })
                .then(async (toast) => {
                  await toast.present();
                });

              // Redirect to the prompt list page
              this.router.navigate(['/prompts']);
            },
            error: (error) => {
              console.log('error', error);

              this.toastController
                .create({
                  message: 'Error updating prompt',
                  position: 'top',
                  duration: 2000,
                })
                .then(async (toast) => {
                  await toast.present();
                });
            },
          });
      } else {
        console.log('not accepted');
      }
    });
  }

  isPromptActive() {
    if (this.start.disabled && this.end.disabled) return true;

    // If end time is disabled and start time is before current time, return true
    if (this.end.disabled && this.isAfterNow(this.start.isoTime)) {
      return true;
    }

    // If start time is disabled and end time is after current time, return true
    if (this.start.disabled && this.isBeforeNow(this.end.isoTime)) {
      return true;
    }

    // Both are assumed to be enabled

    if (!this.changeableIsStart) {
      // Check whether end time is after start time and if so, change start time to day before end time
      if (this.isBefore(this.end.isoTime, this.start.isoTime)) {
        this.start.isoTime = this.subtractDaysIso(this.end.isoTime, 1);
        this.start.datetime = this.toUnixSeconds(this.start.isoTime);
        console.log(this.start.isoTime);
      }
    } else {
      // Check whether start time is after end time and if so, change end time to day after start time
      if (this.isAfter(this.start.isoTime, this.end.isoTime)) {
        this.end.isoTime = this.addDaysIso(this.start.isoTime, 1);
        this.end.datetime = this.toUnixSeconds(this.end.isoTime);
        console.log(this.end.isoTime);
      }
    }

    // Return true if current time is between start and end time
    return this.isNowBetween(this.start.isoTime, this.end.isoTime);
  }

  getMaxTime(isStart: boolean) {
    if (isStart) {
      return this.end.disabled ? this.absoluteMaxDate : this.end.isoTime;
    } else {
      return this.absoluteMaxDate;
    }
  }

  getMinTime(isStart: boolean) {
    if (isStart) {
      return this.absoluteMinDate;
    } else {
      return this.start.disabled ? this.absoluteMinDate : this.start.isoTime;
    }
  }

  getRoundTime(time: number | string) {
    const date = this.parseDate(time);
    if (!date) {
      return this.toLocalIsoString(new Date());
    }

    const rounded = new Date(date.getTime());
    rounded.setSeconds(0, 0);
    rounded.setMinutes(Math.round(rounded.getMinutes() / 15) * 15);

    if (rounded.getMinutes() === 60) {
      rounded.setHours(rounded.getHours() + 1, 0, 0, 0);
    }

    return this.toLocalIsoString(rounded);
  }

  formatPrompt(text: string) {
    // Remove <speak> tags
    return text.replace('<speak>', '').replace('</speak>', '');
  }

  onBlur($event, isStart: boolean) {
    // Round selected value to nearest 15 minutes.
    const selectedValue = this.getEventDateTimeValue($event);
    if (!selectedValue) {
      return;
    }

    const rounded = this.getRoundTime(selectedValue);
    // Set time to rounded time
    if (isStart) {
      this.start.isoTime = rounded;
      console.log(this.toUnixSeconds(rounded));
      this.start.datetime = this.toUnixSeconds(rounded);
    } else {
      this.end.isoTime = rounded;
      console.log(this.toUnixSeconds(rounded));
      this.end.datetime = this.toUnixSeconds(rounded);
    }

    this.ensureDistinctDateTimes(isStart);
  }

  private unixNow(): number {
    return Math.floor(Date.now() / 1000);
  }

  private parseDate(value: number | string | Date): Date | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'number') {
      const date = new Date(this.normalizeEpochToMs(value));
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const numeric = Number(value);
    if (!Number.isNaN(numeric) && `${numeric}` === value.trim()) {
      const date = new Date(this.normalizeEpochToMs(numeric));
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private normalizeEpochToMs(value: number): number {
    // Treat sub-trillion epoch values as seconds, otherwise as milliseconds.
    return Math.abs(value) < 1_000_000_000_000 ? value * 1000 : value;
  }

  private getEventDateTimeValue(event: Event): string | null {
    const customEvent = event as CustomEvent<{ value?: string | string[] | null }>;
    const detailValue = customEvent?.detail?.value;

    if (Array.isArray(detailValue)) {
      return detailValue[0] ?? null;
    }

    if (typeof detailValue === 'string' && detailValue.trim() !== '') {
      return detailValue;
    }

    const target = event.target as HTMLInputElement | { value?: string | null } | null;
    const targetValue = target?.value;

    return typeof targetValue === 'string' && targetValue.trim() !== '' ? targetValue : null;
  }

  private toUnixSeconds(value: number | string | Date): number {
    const date = this.parseDate(value);
    return date ? Math.floor(date.getTime() / 1000) : this.unixNow();
  }

  private addDaysIso(value: number | string | Date, days: number): string {
    const date = this.parseDate(value) ?? new Date();
    date.setDate(date.getDate() + days);
    return this.toLocalIsoString(date);
  }

  private addMinutesIso(value: number | string | Date, minutes: number): string {
    const date = this.parseDate(value) ?? new Date();
    date.setMinutes(date.getMinutes() + minutes);
    return this.toLocalIsoString(date);
  }

  private subtractDaysIso(value: number | string | Date, days: number): string {
    return this.addDaysIso(value, -days);
  }

  private ensureDistinctDateTimes(isStart: boolean): void {
    if (this.start.disabled || this.end.disabled) {
      return;
    }

    if (this.start.isoTime !== this.end.isoTime) {
      return;
    }

    if (isStart) {
      this.end.isoTime = this.addMinutesIso(this.start.isoTime, 15);
      this.end.datetime = this.toUnixSeconds(this.end.isoTime);
      return;
    }

    this.start.isoTime = this.addMinutesIso(this.end.isoTime, -15);
    this.start.datetime = this.toUnixSeconds(this.start.isoTime);
  }

  private toLocalIsoString(value: number | string | Date): string {
    const date = this.parseDate(value) ?? new Date();
    const pad = (num: number) => `${Math.trunc(Math.abs(num))}`.padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const offsetHours = pad(Math.floor(Math.abs(offsetMinutes) / 60));
    const offsetMins = pad(Math.abs(offsetMinutes) % 60);

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMins}`;
  }

  private toMilliseconds(value: number | string): number | null {
    const date = this.parseDate(value);
    return date ? date.getTime() : null;
  }

  private isAfter(left: number | string, right: number | string): boolean {
    const l = this.toMilliseconds(left);
    const r = this.toMilliseconds(right);
    return l !== null && r !== null && l > r;
  }

  private isBefore(left: number | string, right: number | string): boolean {
    const l = this.toMilliseconds(left);
    const r = this.toMilliseconds(right);
    return l !== null && r !== null && l < r;
  }

  private isAfterNow(value: number | string): boolean {
    const ms = this.toMilliseconds(value);
    return ms !== null && Date.now() > ms;
  }

  private isBeforeNow(value: number | string): boolean {
    const ms = this.toMilliseconds(value);
    return ms !== null && Date.now() < ms;
  }

  private isNowBetween(start: number | string, end: number | string): boolean {
    const startMs = this.toMilliseconds(start);
    const endMs = this.toMilliseconds(end);
    const nowMs = Date.now();
    return startMs !== null && endMs !== null && nowMs > startMs && nowMs < endMs;
  }
}
